import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface MaintenanceConfig {
  id: number;
  is_active: boolean;
  message: string;
  started_at: string | null;
  duration_minutes: number;
  ends_at: string | null;
  warning_minutes: number;
  activated_by_admin_id: number | null;
}

interface MaintenanceModeManagerProps {
  currentUser: any;
}

export const MaintenanceModeManager: React.FC<MaintenanceModeManagerProps> = ({ currentUser }) => {
  const [config, setConfig] = useState<MaintenanceConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [warningMinutes, setWarningMinutes] = useState(5);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  useEffect(() => {
    loadMaintenanceConfig();
  }, []);

  useEffect(() => {
    if (!config?.is_active || !config?.ends_at) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const end = new Date(config.ends_at!).getTime();
      const distance = end - now;

      if (distance < 0) {
        setTimeRemaining('Expired');
        loadMaintenanceConfig(); // Reload to check if auto-deactivated
        return;
      }

      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [config]);

  const loadMaintenanceConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('site_maintenance')
        .select('*')
        .order('id', { ascending: false })
        .limit(1);

      if (error) throw error;

      const record = data && data.length > 0 ? data[0] : null;

      setConfig(record);
      if (record) {
        setMessage(record.message || '');
        setDurationMinutes(record.duration_minutes || 30);
        setWarningMinutes(record.warning_minutes || 5);
      }
    } catch (error) {
      console.error('Error loading maintenance config:', error);
    } finally {
      setLoading(false);
    }
  };

  const activateMaintenanceMode = async () => {
    try {
      setLoading(true);

      const now = new Date();
      const warningEndsAt = new Date(now.getTime() + warningMinutes * 60000);
      const maintenanceEndsAt = new Date(warningEndsAt.getTime() + durationMinutes * 60000);

      // Extract numeric ID from currentUser (handle both numeric and UUID formats)
      const numericAdminId = typeof currentUser?.id === 'number'
        ? currentUser.id
        : (currentUser?.numeric_id || null);

      const { error } = await supabase
        .from('site_maintenance')
        .update({
          is_active: true,
          message: message,
          started_at: warningEndsAt.toISOString(),
          duration_minutes: durationMinutes,
          ends_at: maintenanceEndsAt.toISOString(),
          warning_minutes: warningMinutes,
          activated_by_admin_id: numericAdminId,
          updated_at: new Date().toISOString()
        })
        .eq('id', config?.id);

      if (error) throw error;

      alert(`✅ Maintenance mode activated!\n\nWarning period: ${warningMinutes} minutes\nMaintenance starts at: ${warningEndsAt.toLocaleTimeString()}\nMaintenance ends at: ${maintenanceEndsAt.toLocaleTimeString()}`);
      
      await loadMaintenanceConfig();
      setShowConfirmDialog(false);
    } catch (error) {
      console.error('Error activating maintenance mode:', error);
      alert('❌ Failed to activate maintenance mode: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const deactivateMaintenanceMode = async () => {
    if (!confirm('Are you sure you want to end maintenance mode now?')) return;

    try {
      setLoading(true);

      // Extract numeric ID from currentUser (handle both numeric and UUID formats)
      const numericAdminId = typeof currentUser?.id === 'number'
        ? currentUser.id
        : (currentUser?.numeric_id || null);

      const { error } = await supabase
        .from('site_maintenance')
        .update({
          is_active: false,
          deactivated_at: new Date().toISOString(),
          deactivated_by_admin_id: numericAdminId,
          updated_at: new Date().toISOString()
        })
        .eq('id', config?.id);

      if (error) throw error;

      alert('✅ Maintenance mode deactivated successfully!');
      await loadMaintenanceConfig();
    } catch (error) {
      console.error('Error deactivating maintenance mode:', error);
      alert('❌ Failed to deactivate maintenance mode: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !config) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
        <p className="mt-4 text-white">Loading maintenance configuration...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <span className="mr-3">🔧</span>
          Maintenance Mode Manager
        </h2>
        {config?.is_active && (
          <span className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold animate-pulse">
            ACTIVE
          </span>
        )}
      </div>

      {/* CRITICAL: Admin Safety Notice */}
      <div className="p-4 bg-blue-900/30 border border-blue-500 rounded-lg">
        <h3 className="text-lg font-bold text-blue-400 mb-2">🛡️ Admin Safety Features</h3>
        <ul className="text-sm text-blue-200 space-y-1">
          <li>✅ You can ALWAYS access admin panel via <code className="bg-blue-950 px-2 py-1 rounded">aureus.africa/?admin=true</code></li>
          <li>✅ You will NOT be logged out during maintenance mode</li>
          <li>✅ You can deactivate maintenance mode at any time from this panel</li>
          <li>⚠️ Regular users will be logged out and blocked during maintenance</li>
        </ul>
      </div>

      {/* Current Status */}
      {config?.is_active && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-500 mb-4">⚠️ Maintenance Mode Active</h3>
          <div className="space-y-2 text-gray-300">
            <p><strong>Message:</strong> {config.message}</p>
            <p><strong>Started:</strong> {config.started_at ? new Date(config.started_at).toLocaleString() : 'N/A'}</p>
            <p><strong>Ends:</strong> {config.ends_at ? new Date(config.ends_at).toLocaleString() : 'N/A'}</p>
            <p><strong>Time Remaining:</strong> <span className="text-2xl font-bold text-red-500">{timeRemaining}</span></p>
          </div>
          <button
            onClick={deactivateMaintenanceMode}
            disabled={loading}
            className="mt-4 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
          >
            End Maintenance Now
          </button>
        </div>
      )}

      {/* Configuration Form */}
      {!config?.is_active && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-white mb-4">Configure Maintenance Mode</h3>

          {/* Message Input */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Maintenance Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              placeholder="e.g., We are upgrading our commission system. Expected completion: 30 minutes"
            />
          </div>

          {/* Duration Selector */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Maintenance Duration (minutes)
              </label>
              <select
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Number(e.target.value))}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-500"
              >
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={240}>4 hours</option>
                <option value={480}>8 hours</option>
                <option value={720}>12 hours</option>
                <option value={1440}>24 hours</option>
                <option value={2160}>36 hours</option>
                <option value={2880}>48 hours</option>
                <option value={4320}>72 hours</option>
              </select>
            </div>

            {/* Warning Time Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Warning Period (minutes)
              </label>
              <select
                value={warningMinutes}
                onChange={(e) => setWarningMinutes(Number(e.target.value))}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-500"
              >
                <option value={1}>1 minute</option>
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
              </select>
            </div>
          </div>

          {/* Activate Button */}
          <button
            onClick={() => setShowConfirmDialog(true)}
            disabled={loading || !message.trim()}
            className="w-full px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Activate Maintenance Mode
          </button>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">⚠️ Confirm Maintenance Mode</h3>
            <div className="space-y-3 text-gray-300 mb-6">
              <p>This will:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Show warning banner to all users in <strong>{warningMinutes} minutes</strong></li>
                <li>Force logout all users after warning period</li>
                <li>Block all login/registration attempts</li>
                <li>Display maintenance screen for <strong>{durationMinutes} minutes</strong></li>
              </ul>
              <p className="text-yellow-500 font-semibold mt-4">
                Are you sure you want to continue?
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={activateMaintenanceMode}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                Activate Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

